import { createContext, Fragment, useContext, useEffect, useState } from "react";
import DICT from "@/lib/i18n-dict";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const LANGS = [["ar", "العربية"], ["en", "English"], ["hi", "हिन्दी"], ["bn", "বাংলা"]];
const IDX = { en: 0, hi: 1, bn: 2 };
let current = "ar";
export const getLang = () => current;

export function tr(s, vars) {
  let out = current !== "ar" && DICT[s] ? DICT[s][IDX[current]] || s : s;
  if (vars) Object.entries(vars).forEach(([k, v]) => { out = out.split(`{${k}}`).join(v); });
  return out;
}

const Ctx = createContext({ lang: "ar", setLang: () => {} });

export function LangProvider({ children }) {
  const { user } = useAuth();
  const [chosen, setChosen] = useState(localStorage.getItem("lang") || "");
  const lang = user && user.role !== "driver" ? "ar" : chosen || user?.lang || "ar";
  current = lang;
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);
  useEffect(() => {
    if (user?.role === "driver" && chosen && user.lang !== chosen) { api.put("/auth/lang", { lang: chosen }).catch(() => {}); }
  }, [user, chosen]);
  const setLang = (l) => { localStorage.setItem("lang", l); setChosen(l); };
  return <Ctx.Provider value={{ lang, setLang }}><Fragment key={lang}>{children}</Fragment></Ctx.Provider>;
}

export const useLang = () => useContext(Ctx);
export function useT() {
  useContext(Ctx);
  return tr;
}

const mem = (() => { try { return JSON.parse(localStorage.getItem("tr_cache") || "{}"); } catch { return {}; } })();
const pending = {};
let timer;

async function flush() {
  for (const lang of Object.keys(pending)) {
    const m = pending[lang];
    delete pending[lang];
    const texts = [...m.keys()];
    for (let i = 0; i < texts.length; i += 50) {
      const chunk = texts.slice(i, i + 50);
      let items = chunk;
      try { items = (await api.post("/i18n/translate", { texts: chunk, lang })).data.items; } catch { items = chunk; }
      chunk.forEach((t, j) => { if (items[j] && items[j] !== t) mem[`${lang}|${t}`] = items[j]; m.get(t).forEach((r) => r(items[j] || t)); });
    }
  }
  try { localStorage.setItem("tr_cache", JSON.stringify(mem)); } catch { /* storage full */ }
}

function request(text, lang) {
  return new Promise((res) => {
    const m = (pending[lang] = pending[lang] || new Map());
    if (!m.has(text)) m.set(text, []);
    m.get(text).push(res);
    clearTimeout(timer);
    timer = setTimeout(flush, 60);
  });
}

export function useAuto(text) {
  const { lang } = useLang();
  const k = `${lang}|${text}`;
  const skip = lang === "ar" || !text || !/[\u0600-\u06FF]/.test(text) || DICT[text] || mem[k];
  const [got, setGot] = useState({});
  useEffect(() => {
    if (skip) { return undefined; }
    let on = true;
    request(text, lang).then((o) => { if (on) { setGot({ [k]: o }); } });
    return () => { on = false; };
  }, [k, skip, text, lang]);
  if (lang === "ar" || !text) return text;
  if (DICT[text]) return DICT[text][IDX[lang]];
  return mem[k] || got[k] || text;
}

export const Auto = ({ text }) => <>{useAuto(text)}</>;

export function LangPicker({ dark }) {
  const { lang, setLang } = useLang();
  return (
    <div className={`grid grid-cols-4 gap-1.5 p-1 rounded-2xl ${dark ? "bg-white/10" : "bg-slate-100"}`} data-testid="lang-picker">
      {LANGS.map(([k, l]) => (
        <button key={k} type="button" data-testid={`lang-${k}`} onClick={() => setLang(k)}
          className={`h-10 rounded-xl text-sm font-bold transition-colors ${lang === k ? (dark ? "bg-white text-brand" : "bg-brand text-white") : dark ? "text-white/85" : "text-slate-600"}`}>{l}</button>
      ))}
    </div>
  );
}
